/* global gtag, fbq */
import { useState, useEffect } from 'react';
import { Check, MapPin, Clock, Star, Users, Smartphone, Gift, Menu, X } from 'lucide-react';
import { ref, set } from 'firebase/database';
import { db } from '../firebaseConfig';
import { Helmet } from 'react-helmet-async';

export default function BuyMexicoCity() {
  const [purchaseType, setPurchaseType] = useState('self'); // 'self' or 'gift'
  const [giftTo, setGiftTo] = useState('');
  const [giftFrom, setGiftFrom] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Countdown timer - resets at midnight each day
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - now;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Show sticky CTA on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const nearBottom = scrollY + window.innerHeight > document.documentElement.scrollHeight - 300;
      setShowStickyCTA(scrollY > 300 && !nearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = async (location) => {
    // Track the click in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: 23.99,
        items: [{
          item_id: 'mexico-city-tour',
          item_name: 'Mexico City Scavenger Hunt Tour',
          price: 23.99,
          quantity: 1
        }],
        purchase_type: purchaseType,
        cta_location: location
      });
    }
    // Track in Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_ids: ['mexico-city-tour'],
        content_type: 'product',
        value: 23.99,
        currency: 'USD'
      });
    }

    if (purchaseType === 'self') {
      // Regular purchase - use original link
      window.location.href = 'https://buy.stripe.com/28E3coaRobrybRZ6WJcZa02?prefilled_promo_code=EXPLORE2026';
    } else {
      // Gift purchase - store personalization and redirect to gift link
      setIsProcessing(true);

      try {
        // Generate unique ID for this gift purchase intent
        const intentId = `gift_${Date.now()}_${Math.random().toString(36).substring(7)}`;

        // Store personalization data in Firebase temporarily
        await set(ref(db, `giftIntents/${intentId}`), {
          to: giftTo,
          from: giftFrom,
          message: giftMessage,
          createdAt: Date.now(),
          expiresAt: Date.now() + (60 * 60 * 1000) // Expires in 1 hour
        });

        // Redirect to gift Stripe link with intent ID
        window.location.href = `https://buy.stripe.com/9B6fZa8JggLSe0794RcZa03?prefilled_promo_code=EXPLORE2026&client_reference_id=${intentId}`;
      } catch (error) {
        console.error('Error storing gift data:', error);
        console.error('Error details:', error.message, error.code);
        alert(`Failed to process gift purchase: ${error.message}\n\nPlease check the console for details or contact support.`);
        setIsProcessing(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Mexico City Scavenger Hunt Adventure Tour - Tour in a Box</title>
        <meta name="description" content="Interactive Mexico City scavenger hunt tour through Polanco. Solve riddles, uncover history, and recover Montezuma's treasure. Book your adventure for $23.99!" />
        <meta name="keywords" content="Mexico City scavenger hunt, Polanco walking tour, Mexico City treasure hunt, Montezuma treasure tour, interactive Mexico City tour, things to do in Polanco" />
        <link rel="canonical" href="https://play.tourinabox.com/buy/mexico-city" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://play.tourinabox.com/buy/mexico-city" />
        <meta property="og:title" content="Mexico City Scavenger Hunt - Montezuma's Treasure Adventure" />
        <meta property="og:description" content="Interactive tour through Polanco. Solve riddles, uncover history, and recover Montezuma's treasure. 10 historic stops in 1 hour. Perfect for families & groups." />
        <meta property="og:image" content="https://play.tourinabox.com/images/mexicocity.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Mexico City Polanco neighborhood streets and landmarks" />
        <meta property="og:site_name" content="Tour in a Box" />
        <meta property="og:locale" content="en_US" />
        <meta property="product:price:amount" content="23.99" />
        <meta property="product:price:currency" content="USD" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://play.tourinabox.com/buy/mexico-city" />
        <meta name="twitter:title" content="Mexico City Scavenger Hunt - Montezuma's Treasure" />
        <meta name="twitter:description" content="Interactive Mexico City tour through Polanco. Solve riddles, uncover history at 10 historic stops. Self-paced adventure in 1 hour." />
        <meta name="twitter:image" content="https://play.tourinabox.com/images/mexicocity.webp" />
        <meta name="twitter:image:alt" content="Mexico City Polanco neighborhood streets and landmarks" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "Mexico City Scavenger Hunt Adventure Tour",
            "description": "Interactive walking tour through Mexico City's Polanco neighborhood with 10 riddle-based stops. Hunt for Montezuma's treasure while exploring historic landmarks.",
            "brand": {
              "@type": "Brand",
              "name": "Tour in a Box"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://play.tourinabox.com/buy/mexico-city",
              "priceCurrency": "USD",
              "price": "23.99",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Tour in a Box LLC"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "image": "https://play.tourinabox.com/images/mexicocity.webp",
            "category": "Tours & Activities"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://play.tourinabox.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Mexico City Tour",
                "item": "https://play.tourinabox.com/buy/mexico-city"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Urgency Banner with Countdown */}
      <div className="py-2 px-4" style={{ background: '#F8662B' }}>
        <div className="flex items-center justify-center gap-3">
          <Star size={12} fill="white" style={{ color: 'white' }} />
          <span className="text-sm font-bold text-white" style={{ fontFamily: 'Rubik, sans-serif' }}>
            Sale ends in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
          </span>
          <Star size={12} fill="white" style={{ color: 'white' }} />
          <span className="text-sm font-bold text-white" style={{ fontFamily: 'Rubik, sans-serif' }}>20% Off</span>
          <Star size={12} fill="white" style={{ color: 'white' }} />
        </div>
      </div>

      {/* Header with Logo */}
      <header className="pt-8 pb-6 px-4 border-b border-gray-200 relative" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <img
            src="/PrimaryLogo.png"
            alt="Tour in a Box"
            className="h-8 sm:h-10"
            style={{ objectFit: 'contain', width: 'auto' }}
          />

          {/* Menu Button */}
          <div className="absolute right-4 top-8">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <Menu size={20} style={{ color: '#413C04' }} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                style={{ fontFamily: 'Urbanist, sans-serif' }}
              >
                <a
                  href="https://www.tourinabox.com/store"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Browse All Tours
                </a>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(255, 250, 223, 0.5))' }}>
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url(/images/noise-512.png)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 sm:pt-8 sm:pb-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Column - Copy */}
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Mexico City
              </h1>

              <p className="text-2xl font-bold mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#E4B4A6' }}>
                Scavenger Hunt Adventure
              </p>

              {/* Social Proof */}
              <div
                className="flex items-center gap-4 mb-6 cursor-pointer transition-opacity hover:opacity-80"
                onClick={() => {
                  document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex -space-x-2">
                  <img src="/images/customer1.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer2.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer3.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div className="text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <div className="flex items-center gap-1" style={{ color: '#F8662B' }}>
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                    </div>
                    <span className="font-semibold" style={{ color: '#413C04' }}>4.8 out of 5</span>
                  </div>
                  <p style={{ color: '#413C04', opacity: 0.7 }}>
                    Loved by <span className="font-semibold" style={{ color: '#413C04', opacity: 1 }}>locals and visitors</span> alike
                  </p>
                </div>
              </div>

              <p className="text-lg mb-6" style={{ color: '#413C04', opacity: 0.75 }}>
                A self-guided walking game with riddles across 10 historic landmarks in Polanco neighborhood
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>No app download required</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>1+ hour self-paced adventure</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>Play instantly on web or anytime in next year</span>
                </div>
              </div>

              {/* Pricing CTA */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2" style={{ borderColor: '#E4B4A6' }}>
                <div className="flex items-baseline justify-center gap-3 mb-2">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$23.99</span>
                  <span className="text-xl text-gray-400 line-through">$30.00</span>
                  <span className="bg-red-100 text-red-700 text-sm font-bold px-2 py-1 rounded">
                    Save 20%
                  </span>
                </div>

                {/* Gift Personalization Form (conditional) */}
                {purchaseType === 'gift' && (
                  <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: '#FFFADF' }}>
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                      <Gift size={16} />
                      Personalize Your Gift (Optional)
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          To
                        </label>
                        <input
                          type="text"
                          value={giftTo}
                          onChange={(e) => setGiftTo(e.target.value)}
                          placeholder="Recipient's name"
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          From
                        </label>
                        <input
                          type="text"
                          value={giftFrom}
                          onChange={(e) => setGiftFrom(e.target.value)}
                          placeholder="Your name"
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          Personal Message
                        </label>
                        <textarea
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          placeholder="Add a personal message..."
                          rows={3}
                          maxLength={200}
                          className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                        <p className="text-xs mt-1" style={{ color: '#385B37', opacity: 0.6, fontFamily: 'Urbanist, sans-serif' }}>
                          {giftMessage.length}/200 characters
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleBuyNow('hero')}
                  disabled={isProcessing}
                  className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: '#F8662B',
                    fontFamily: 'Rubik, sans-serif'
                  }}
                  onMouseEnter={(e) => !isProcessing && (e.currentTarget.style.background = '#E05520')}
                  onMouseLeave={(e) => !isProcessing && (e.currentTarget.style.background = '#F8662B')}
                >
                  {isProcessing ? 'Processing...' : (purchaseType === 'gift' ? '🎁 Continue to Checkout' : 'Get My Tour')}
                </button>

                {/* Gift Toggle Link */}
                {purchaseType === 'self' ? (
                  <button
                    onClick={() => setPurchaseType('gift')}
                    className="w-full text-sm mt-2 py-1 transition-all hover:underline"
                    style={{ color: '#8FB3D3', fontFamily: 'Urbanist, sans-serif' }}
                  >
                    Buying as a gift?
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setPurchaseType('self');
                      setGiftTo('');
                      setGiftFrom('');
                      setGiftMessage('');
                    }}
                    className="w-full text-sm mt-2 py-1 transition-all hover:underline"
                    style={{ color: '#8FB3D3', fontFamily: 'Urbanist, sans-serif' }}
                  >
                    ← Back to regular purchase
                  </button>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    Secure checkout with Stripe
                  </span>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    1 year access
                  </span>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    30-day refund
                  </span>
                </div>
              </div>

            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/mexicocity.webp"
                  alt="Mexico City colorful streets"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Why Explorers Love This Tour
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <MapPin size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Landmark-Based Directions</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Navigate using landmarks, not GPS
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                <Clock size={24} style={{ color: '#385B37' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Go At Your Pace</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Pause anytime for photos, food, or shopping.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Smartphone size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Works Offline</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Download once and use without internet connection
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Star size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Engaging Riddles</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Fun challenges at each stop with hints if you need them
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(228, 180, 166, 0.4)' }}>
                <Users size={24} style={{ color: '#E4B4A6' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Perfect for Everyone</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Great for couples, families, friends, or solo adventurers
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Gift size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Great for Gifting</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Select gift option for personalized, printable gift certificate
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price Comparison */}
      <section className="py-16" style={{ background: '#FFFADF' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            How We Compare
          </h2>
          <p className="text-center mb-10" style={{ color: '#413C04', opacity: 0.7 }}>
            See how we stack up against other Mexico City experiences
          </p>

          <div className="rounded-2xl overflow-hidden shadow-lg border" style={{ borderColor: '#E4B4A6' }}>
            {/* Header row */}
            <div className="grid grid-cols-4 text-center text-sm font-bold" style={{ background: '#413C04', color: 'white', fontFamily: 'Rubik, sans-serif' }}>
              <div className="py-3 px-2"></div>
              <div className="py-3 px-2 relative" style={{ background: '#F8662B' }}>
                Tour in a Box
              </div>
              <div className="py-3 px-2">Walking Tour</div>
              <div className="py-3 px-2">Escape Room</div>
            </div>

            {/* Price row */}
            <div className="grid grid-cols-4 text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-left pl-4 flex flex-col justify-center" style={{ color: '#413C04' }}>
                <div className="text-sm font-semibold" style={{ fontFamily: 'Rubik, sans-serif' }}>Price</div>
                <div className="text-xs leading-tight" style={{ opacity: 0.5 }}>for a group of 3</div>
              </div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}>
                <span className="text-lg font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>$24</span>
              </div>
              <div className="px-2 flex flex-col items-center justify-center">
                <span className="text-lg font-bold" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>$90</span>
                <span className="text-xs leading-tight" style={{ color: '#413C04', opacity: 0.5 }}>$30/person</span>
              </div>
              <div className="px-2 flex flex-col items-center justify-center">
                <span className="text-lg font-bold" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>$75</span>
                <span className="text-xs leading-tight" style={{ color: '#413C04', opacity: 0.5 }}>$25/person</span>
              </div>
            </div>

            {/* Private */}
            <div className="grid grid-cols-4 text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Private</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
            </div>

            {/* Your own pace */}
            <div className="grid grid-cols-4 text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Go at your own pace</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Play anytime */}
            <div className="grid grid-cols-4 text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Play anytime</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Fun & educational */}
            <div className="grid grid-cols-4 text-center items-stretch bg-white min-h-[52px]">
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Fun & educational</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="text-xs font-semibold" style={{ color: '#385B37' }}>Both</span></div>
              <div className="px-2 flex items-center justify-center"><span className="text-xs" style={{ color: '#999' }}>Educational</span></div>
              <div className="px-2 flex items-center justify-center"><span className="text-xs" style={{ color: '#999' }}>Fun</span></div>
            </div>
          </div>

          <p className="text-center text-sm mt-4" style={{ color: '#413C04', opacity: 0.6 }}>
            Most groups of 3 or fewer play on one device, so you only need to buy once
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16" style={{ background: 'linear-gradient(to bottom right, rgba(255, 250, 223, 0.6), rgba(228, 180, 166, 0.4))' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            What Explorers Are Saying
          </h2>
          <p className="text-center mb-12" style={{ color: '#413C04', opacity: 0.7 }}>
            Real reviews from real explorers
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Our group loved the scavenger-hunt feel of the tour. Great idea and product. 10/10."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #8FB3D3, #6A92B3)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Verified Customer</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "We loved the little surprise at the end. I'd buy a tour for every city I visit."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #385B37, #2A4428)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Verified Customer</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Loved using directions based on landmarks. It forced us to look up and notice the city."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #F8662B, #E05520)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Verified Customer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Frequently Asked Questions
          </h2>

          <style>{`
            details summary {
              list-style: none;
              position: relative;
              padding-right: 2rem;
            }
            details summary::-webkit-details-marker {
              display: none;
            }
            details summary::after {
              content: '+';
              position: absolute;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              font-size: 1.5rem;
              font-weight: bold;
              color: #413C04;
              transition: transform 0.2s;
            }
            details[open] summary::after {
              content: '−';
            }
          `}</style>

          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Where does the tour start?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour begins at the JW Marriott in Polanco. Once you purchase, you'll receive detailed instructions on the exact starting point.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                How long does the tour take?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour typically takes about 1 hour, but you can go at your own pace! Take breaks for photos, food, or shopping. Your progress is automatically saved.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Do I need to download an app?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                No! The tour works directly in your web browser on any smartphone. You can also add it to your home screen for offline access.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                When does my access expire?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Your access is valid for 1 full year from purchase. Start the tour whenever you're ready - today, next week, or next month!
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Can multiple people use one purchase?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Each purchase unlocks access for one account. Groups or families can play together on a single device, but many prefer buying access for multiple accounts so everyone can play along on their own phone.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What if I get stuck on a riddle?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Every location has a hint button! After one minute, you can unlock a helpful hint that will guide you toward the answer.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What's your refund policy?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                We offer a 30-day money-back guarantee. If you're not satisfied with your tour experience, contact us for a full refund.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What languages is this available in?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour is currently available in English. Spanish language support is coming soon!
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#F8662B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Rubik, sans-serif' }}>
            Ready for an Adventure?
          </h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join adventurers who've discovered Mexico City in a whole new way
          </p>

          <div className="bg-white rounded-2xl p-6 max-w-md mx-auto shadow-2xl">
            <div className="flex items-baseline justify-center gap-3 mb-2">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$23.99</span>
              <span className="text-2xl text-gray-400 line-through">$30.00</span>
            </div>
            <button
              onClick={() => handleBuyNow('bottom')}
              className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 mb-4"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2A2702'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#413C04'}
            >
              Get My Tour
            </button>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>✓ Secure checkout with Stripe</span>
              <span>✓ 1 year access</span>
              <span>✓ No app download</span>
              <span>✓ 30-day refund</span>
            </div>
          </div>

          <p className="mt-6 text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Secure checkout powered by Stripe
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#413C04' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm" style={{ color: 'rgba(255, 250, 223, 0.7)' }}>
              © {new Date().getFullYear()} Tour in a Box LLC
            </div>
            <div className="flex gap-6 text-sm">
              <a href="mailto:hello@tourinabox.com" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Contact
              </a>
              <a href="https://www.tourinabox.com/policies" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Privacy
              </a>
              <a href="https://www.instagram.com/unboxyourtour" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile-Only Sticky Bottom CTA */}
      {showStickyCTA && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50" style={{ borderColor: '#E4B4A6' }}>
          <div className="px-4 py-3">
            <button
              onClick={() => handleBuyNow('sticky')}
              className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
            >
              Get My Tour — $23.99
            </button>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>Secure checkout with Stripe</span>
              <span>•</span>
              <span>30-day refund</span>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}
